import { Divider, Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { validateCoordinates } from '@nx-smartmonkey/shared/helpers';
import { FixedMarkerMap, LatLng, RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import RoutalLogoGreen from '../../../../../assets/img/routal-star-green.svg';
import BackArrowIcon from '../../../../../assets/svg/BackArrowIcon';
import { DividerWithText } from '../../../../../components/DividerWithText';
import { LoadingButton } from '../../../../../components/LoadingButton';
import { Modal } from '../../../../../components/Modal';
import { CreateStopsMethod, EventTrackTypes } from '../../../../../constants/EventTracks';
import * as routing from '../../../../../constants/Routing';
import { Route } from '../../../../../domain/route/Route';
import { Stop } from '../../../../../domain/stop/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveLanguage } from '../../../../../hooks/translations/useRetrieveActiveLanguage';
import { useCaptureEvents } from '../../../../../hooks/useCaptureEvents';
import { useInterval } from '../../../../../hooks/useInterval';
import { Text } from '../../../../../react-components/Text';
import { getLocationFromCoordinates } from '../../../../../services/requests';
import { FREE_STOP_LIMIT } from '../../../components/upgradeSubscription/UpgradeSubscriptionComponent';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUpgradeDialogVisibility } from '../../../hooks/useUpgradeDialogVisibility';
import { RouteActionType } from '../../context/actions';
import { useRouteContext } from '../../context/context';
import { useCreateStops } from '../../hooks/useCreateStops';
import { useUpdateRouteStopsOrder } from '../../hooks/useUpdateRouteStopsOrder';
import { CreateAndReorderStopsMap } from '../CreateAndReorderStopsMap';
import { useRetrieveStaticHeight } from '../hooks/useRetrieveStaticHeight';
import { StopsListContainer } from '../StopsListContainer';
import { AddedStopPill } from './AddedStopPill';

interface AddedStopsProps {
  selectedRoute: Route;
  addedStops: Stop[];
  method: CreateStopsMethod;
  onOpenAddMoreStops: () => void;
  onAddLocation: (location: Location) => void;
  onCreatedAndOptimized?: () => void;
}

export const AddedStops = ({
  selectedRoute,
  addedStops,
  method,
  onOpenAddMoreStops,
  onAddLocation,
  onCreatedAndOptimized,
}: AddedStopsProps) => {
  const navigate = useNavigate();
  const { capture } = useCaptureEvents();
  const { routeDispatch } = useRouteContext();
  const {
    run,
    isLoading: reOptimizeLoading,
    success: reOptimizeSuccess,
    error: reOptimizeError,
  } = useUpdateRouteStopsOrder({ driverRoute: selectedRoute });
  const { mapHeight, contentHeight } = useRetrieveStaticHeight();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const [dialog, setDialog] = useState<
    | `reoptimize`
    | `update`
    | `loading`
    | `optimization_error`
    | `optimization_conflicts`
    | `create_stop_from_map`
    | null
  >();
  const [mapStop, setMapStop] = useState<Location | null>(null);

  const { isSoloDriver, hasSubscription, canReorderStops } = usePermissions();

  const { setDialogVisibility } = useUpgradeDialogVisibility();
  const totalStops = selectedRoute.stops.length + addedStops.length;

  const { isLoading, data: updatedRoute, success, run: createStops, error } = useCreateStops({ route: selectedRoute });

  useEffect(() => {
    if (error || reOptimizeError) {
      setDialog(`optimization_error`);
    }
  }, [error, reOptimizeError]);

  const { reset: resetInterval } = useInterval({
    callback: () => {
      if (isLoading || reOptimizeLoading) setDialog(`optimization_error`);
    },
    enabled: isLoading || reOptimizeLoading,
    delay: 60000,
  });

  useEffect(() => {
    if (isLoading || reOptimizeLoading) {
      setDialog(`loading`);
    }
  }, [isLoading, reOptimizeLoading]);

  useEffect(() => {
    if (success || reOptimizeSuccess) {
      resetInterval();
      const hasAnyOptimizationWarning = updatedRoute?.stops.some((stop) => stop.optimization_warning);
      if (hasAnyOptimizationWarning) {
        setDialog(hasAnyOptimizationWarning ? `optimization_conflicts` : null);
      } else {
        setDialog(null);
        if (onCreatedAndOptimized) onCreatedAndOptimized();
        navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, reOptimizeSuccess]);

  return (
    <>
      <Modal
        open={dialog === `optimization_error` || dialog === `optimization_conflicts`}
        title={{
          intlId: `driver.stops.create_stops.added_stops.${dialog}_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stops.create_stops.added_stops.${dialog}_dialog.subtitle`,
        }}
        buttons={
          dialog === `optimization_error`
            ? [
                {
                  intlId: `driver.stops.create_stops.added_stops.optimization_error_dialog.retry_button`,
                  onClick: () => {
                    run({ stops: [], optimize: true });
                  },
                  disabled: isLoading,
                },
                {
                  intlId: `cancel`,
                  onClick: () => {
                    setDialog(null);
                    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
                    if (onCreatedAndOptimized) onCreatedAndOptimized();
                  },
                  disabled: isLoading,
                  variant: `text`,
                  style: {
                    border: `none`,
                  },
                },
              ]
            : [
                {
                  intlId: `accept`,
                  onClick: () => {
                    setDialog(null);
                    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
                    if (onCreatedAndOptimized) onCreatedAndOptimized();
                  },
                },
              ]
        }
        children={<VSpacer medium />}
      />

      <Modal
        open={dialog === `loading`}
        children={
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            wrap="nowrap"
            style={{ marginTop: `50px`, marginBottom: `60px` }}
          >
            <div style={{ position: `relative` }}>
              <RoutalLoader width={60} strokeWidth={1} />

              <img
                style={{ width: `34px`, top: `28px`, left: `30px`, position: `absolute` }}
                src={RoutalLogoGreen}
                alt=""
                draggable={false}
              />
            </div>

            <Text intlId="driver.stops.create_stops.added_stops.loading_optimization" variant="h6-light" />
          </Grid>
        }
      />

      <Modal
        open={dialog === `reoptimize`}
        onClose={() => {
          setDialog(null);
        }}
        title={{
          intlId: `driver.stops.create_stops.added_stops.reoptimize_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stops.create_stops.added_stops.reoptimize_dialog.subtitle`,
        }}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              capture({
                type: EventTrackTypes.CreateStops,
                payload: {
                  [`Method`]: method,
                  [`Option`]: `Reoptimize`,
                  [`Stop amount`]: addedStops.length,
                },
              });
              createStops({ stops: addedStops, optimize: true });
            },
            disabled: reOptimizeLoading,
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDialog(null);
            },
            disabled: isLoading,
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />

      <Modal
        open={dialog === `update`}
        onClose={() => {
          setDialog(null);
        }}
        title={{
          intlId: `driver.stops.create_stops.added_stops.update_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stops.create_stops.added_stops.update_dialog.subtitle`,
        }}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              capture({
                type: EventTrackTypes.CreateStops,
                payload: {
                  [`Method`]: method,
                  [`Option`]: `Add to end`,
                  [`Stop amount`]: addedStops.length,
                },
              });
              createStops({ stops: addedStops, optimize: false });
            },
            disabled: isLoading,
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDialog(null);
            },
            disabled: isLoading,
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />

      <Modal
        open={dialog === `create_stop_from_map` && mapStop !== null}
        onClose={() => {
          setDialog(null);
        }}
        title={{
          intlId: `driver.stops.create_stops.add_stop_from_map_dialog.title`,
        }}
        subtitle={{
          text: intl.formatMessage(
            {
              id: `driver.stops.create_stops.add_stop_from_map_dialog.subtitle`,
            },
            { value: mapStop?.label }
          ),
        }}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              if (mapStop) onAddLocation(mapStop);
              setDialog(null);
              setMapStop(null);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDialog(null);
              setMapStop(null);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={
          <>
            <Grid container direction="column" alignItems="center" justifyContent="center" style={{ height: `200px` }}>
              {!mapStop ? (
                <RoutalLoader style={{ width: `40px`, height: `40px`, opacity: 1 }} />
              ) : (
                <FixedMarkerMap
                  markerPosition={[mapStop!.lat as number, mapStop!.lng as number]}
                  onPositionChange={_.debounce(async (latLng: LatLng) => {
                    try {
                      const newLocations = await getLocationFromCoordinates({
                        coordinates: [
                          {
                            lat: latLng.lat,
                            lng: latLng.lng,
                          },
                        ],
                        lang: activeLanguage,
                      });

                      if (newLocations[0]) {
                        setMapStop(newLocations[0]);
                      }
                    } catch (error) {
                      console.error(`Error Map AddLocation`, error);
                    }
                  }, 800)}
                  height={200}
                  mapStyles={{
                    borderRadius: `10px`,
                  }}
                />
              )}
            </Grid>

            <VSpacer medium />
          </>
        }
      />

      <Grid container direction="column" wrap="nowrap" style={{ zIndex: 1006 }}>
        <Grid
          style={{
            position: `absolute`,
            top: `0px`,
            left: `0px`,
            zIndex: 1003,
            width: `100%`,
            cursor: `pointer`,
          }}
        >
          <CreateAndReorderStopsMap
            addedStops={addedStops}
            selectedRoute={selectedRoute}
            mapHeight={mapHeight}
            onMarkerClick={(stop: Stop) => {
              if (addedStops.find((s) => s.id === stop.id)) {
                routeDispatch({ type: RouteActionType.SET_EDITING_STOP, payload: stop });
              }
            }}
            onLongClickAddMoreStops={async (location: LatLng) => {
              try {
                let newOptions: Location[] = [];
                if (location.lat && location.lng && validateCoordinates({ lat: location.lat, lng: location.lng })) {
                  newOptions = await getLocationFromCoordinates({
                    coordinates: [
                      {
                        lat: location.lat,
                        lng: location.lng,
                      },
                    ],
                    lang: activeLanguage,
                  });
                }
                if (newOptions[0]) {
                  setMapStop(newOptions[0]);
                  capture({
                    type: EventTrackTypes.OpenCreateStops,
                    payload: {
                      [`Method`]: `Map`,
                    },
                  });
                  setDialog(`create_stop_from_map`);
                }
              } catch (error) {
                console.error(`Error Map AddLocation`, error);
              }
            }}
          />
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{
            position: `absolute`,
            top: `16px`,
            left: `16px`,
            zIndex: 1003,
            height: `50px`,
            width: `50px`,
            backgroundColor: RoutalPalette.white,
            color: `black`,
            borderRadius: `50%`,
            boxShadow: `rgba(0, 0, 0, 0.08) 0px 4px 12px`,
            cursor: `pointer`,
          }}
          onClick={() => onOpenAddMoreStops()}
        >
          <BackArrowIcon style={{ color: RoutalPalette.secondary40, cursor: `pointer` }} />
        </Grid>

        <Grid
          container
          direction="column"
          style={{
            position: `absolute`,
            left: 0,
            right: 0,
            bottom: 0,
            height: `${contentHeight}px`,
            zIndex: 1000,
            backgroundColor: RoutalPalette.white,
          }}
        >
          <Grid
            style={{
              display: `flex`,
              flexDirection: `column`,
              alignItems: `center`,
              padding: `20px`,
              textAlign: `center`,
            }}
          >
            <Text variant="h6" intlId="driver.stops.create_stops.added_stops.title" />
          </Grid>
          <Divider style={{ width: `100%` }} />

          <Grid
            container
            direction="column"
            alignItems="center"
            wrap="nowrap"
            style={{
              padding: `12px 0`,
              overflowY: `auto`,
              flex: 1,
            }}
          >
            <Grid
              container
              direction="column"
              wrap="nowrap"
              style={{
                padding: `0 20px`,
                width: `100%`,
              }}
            >
              {addedStops.map((stop) => (
                <AddedStopPill
                  key={stop.id}
                  location={stop.location!}
                  onClick={() => {
                    routeDispatch({ type: RouteActionType.SET_EDITING_STOP, payload: stop });
                  }}
                />
              ))}
            </Grid>

            {selectedRoute.stops.length > 0 && (
              <>
                <DividerWithText
                  text={intl.formatMessage({ id: `driver.stops.create_stops.added_stops.actual_route` })}
                />

                <StopsListContainer route={selectedRoute} showButtonsIfEmpty={false} />
              </>
            )}
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="space-evenly"
            alignContent="center"
            flexWrap="nowrap"
            alignItems="center"
            style={{
              padding: `12px 20px`,
              height: `70px`,
              borderTop: `1px solid ${RoutalPalette.white}`,
              width: `100%`,
              background: RoutalPalette.white,
            }}
          >
            {selectedRoute.stops.length > 0 ? (
              <>
                <LoadingButton
                  variant="flat"
                  style={{
                    height: `42px`,
                    display: `flex`,
                    flexDirection: `row`,
                    flexWrap: `nowrap`,
                    justifyContent: `center`,
                    alignItems: `center`,
                    borderRadius: `10px`,
                    width: `100%`,
                    marginRight: canReorderStops ? `10px` : `0px`,
                  }}
                  onClick={() => {
                    if (!hasSubscription && totalStops > FREE_STOP_LIMIT && isSoloDriver) {
                      setDialogVisibility(true, `Exceeding limits`);
                    } else {
                      setDialog(`update`);
                    }
                  }}
                >
                  <Text
                    intlId="driver.stops.create_stops.added_stops.add_to_end"
                    style={{ color: RoutalPalette.primary40 }}
                  />
                </LoadingButton>

                {canReorderStops && (
                  <LoadingButton
                    style={{
                      height: `42px`,
                      display: `flex`,
                      flexDirection: `row`,
                      flexWrap: `nowrap`,
                      justifyContent: `center`,
                      alignItems: `center`,
                      borderRadius: `10px`,
                      width: `100%`,
                    }}
                    onClick={() => {
                      if (!hasSubscription && totalStops > FREE_STOP_LIMIT && isSoloDriver) {
                        setDialogVisibility(true, `Exceeding limits`);
                      } else {
                        setDialog(`reoptimize`);
                      }
                    }}
                  >
                    <Text
                      intlId="driver.stops.create_stops.added_stops.reoptimize_button"
                      style={{ color: RoutalPalette.white }}
                    />
                  </LoadingButton>
                )}
              </>
            ) : (
              <LoadingButton
                style={{
                  height: `42px`,
                  display: `flex`,
                  flexDirection: `row`,
                  flexWrap: `nowrap`,
                  justifyContent: `center`,
                  alignItems: `center`,
                  borderRadius: `10px`,
                  width: `100%`,
                }}
                onClick={() => {
                  if (!hasSubscription && totalStops > FREE_STOP_LIMIT && isSoloDriver) {
                    setDialogVisibility(true, `Exceeding limits`);
                  } else {
                    capture({
                      type: EventTrackTypes.CreateStops,
                      payload: {
                        [`Method`]: method,
                        [`Option`]: `Create route`,
                        [`Stop amount`]: addedStops.length,
                      },
                    });
                    createStops({ stops: addedStops, optimize: true });
                  }
                }}
              >
                <Text
                  intlId="driver.stops.create_stops.added_stops.create_route"
                  style={{ color: RoutalPalette.white }}
                />
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
